import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { ASSIGN_WORK_ORDER } from '../../graphql/mutations';
import { GET_USERS, GET_WORK_ORDER } from '../../graphql/queries';
import { GET_ACTIVE_TEAMS } from '../../graphql/teams';
import { UserRole, UserRoleLabels } from '../../utils/enums';
import { canBeAssignedWorkOrders } from '../../utils/role-utils';
import { getAssignableTeamDepartment, getLegacyWorkOrderType, WorkOrderType } from '../../utils/work-order-types';
import { logger } from '../../utils/logger';

// Searchable User Select Component
const SearchableUserSelect = ({
  users,
  selectedUserId,
  onSelect,
  isLoading,
  placeholder = "Search or select a user...",
  emptyMessage = "No users available"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filter users based on search term
  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (UserRoleLabels[user.role] || user.role).toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Find selected user
  const selectedUser = selectedUserId ?
    users.find(user => user.id.toString() === selectedUserId.toString()) : null;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true);
        setHighlightedIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredUsers.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredUsers[highlightedIndex]) {
          handleSelect(filteredUsers[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelect = (user) => {
    onSelect(user.id);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleClear = () => {
    onSelect('');
    setSearchTerm('');
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const displayValue = selectedUser ?
    `${selectedUser.name} (${UserRoleLabels[selectedUser.role] || selectedUser.role})` :
    '';

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={isOpen ? searchTerm : displayValue}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            if (!isOpen) setIsOpen(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            setIsOpen(true);
            setSearchTerm('');
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
          disabled={isLoading}
        />

        {/* Clear button */}
        {selectedUser && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-8 flex items-center text-gray-400 hover:text-gray-600"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <button
          type="button"
          onClick={() => {
            setIsOpen(!isOpen);
            if (!isOpen) {
              setSearchTerm('');
              inputRef.current?.focus();
            }
          }}
          className="absolute inset-y-0 right-0 flex items-center pr-2 text-gray-400 hover:text-gray-600"
        >
          <svg className={`h-5 w-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          {/* Unassigned option */}
          <div
            onClick={() => handleSelect({ id: '' })}
            className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 ${
              highlightedIndex === -1 ? 'bg-blue-50' : ''
            }`}
          >
            <span className="font-normal block truncate text-gray-500">
              Unassigned
            </span>
          </div>

          {/* Loading state */}
          {isLoading && (
            <div className="py-2 pl-3 pr-9 text-gray-500">
              Loading users...
            </div>
          )}

          {/* Users list */}
          {!isLoading && filteredUsers.length > 0 ? (
            filteredUsers.map((user, index) => (
              <div
                key={user.id}
                onClick={() => handleSelect(user)}
                className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-50 ${
                  highlightedIndex === index ? 'bg-blue-50' : ''
                } ${selectedUserId === user.id.toString() ? 'bg-blue-100' : ''}`}
              >
                <div className="flex flex-col">
                  <span className="font-medium block truncate">
                    {user.name}
                  </span>
                  <span className="text-sm text-gray-500 truncate">
                    {UserRoleLabels[user.role] || user.role}
                    {user.areas && user.areas.length > 0 && (
                      <span className="ml-2">
                        • {user.areas.map(area => area.replace(/_/g, ' ')).join(', ')}
                      </span>
                    )}
                  </span>
                </div>

                {/* Selected checkmark */}
                {selectedUserId === user.id.toString() && (
                  <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600">
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
              </div>
            ))
          ) : !isLoading && searchTerm && filteredUsers.length === 0 ? (
            <div className="py-2 pl-3 pr-9 text-gray-500">
              No users found matching "{searchTerm}"
            </div>
          ) : !isLoading && users.length === 0 ? (
            <div className="py-2 pl-3 pr-9 text-gray-500">
              {emptyMessage}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

const AssignWorkOrderModal = ({ workOrder, onClose, onSuccess }) => {
  logger.debug('AssignWorkOrderModal rendered', { workOrderId: workOrder?.id });
  const [assignedToId, setAssignedToId] = useState(workOrder?.assignedToId || '');
  const [assignedTeamId, setAssignedTeamId] = useState(workOrder?.assignedTeamId ? String(workOrder.assignedTeamId) : '');
  const [assignmentType, setAssignmentType] = useState(workOrder?.assignedTeamId ? 'team' : 'person');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Query to get users with appropriate roles
  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS);
  const workOrderType = getLegacyWorkOrderType(workOrder);
  const assignableTeamDepartment = getAssignableTeamDepartment(workOrderType);
  const { data: teamsData } = useQuery(GET_ACTIVE_TEAMS, {
    variables: { department: assignableTeamDepartment },
  });

  // Mutation to assign work order
  const [assignWorkOrder] = useMutation(ASSIGN_WORK_ORDER, {
    onCompleted: (data) => {
      logger.debug('Assignment completed successfully', { workOrderId: data?.assignWorkOrder?.id });
      onSuccess();
    },
    onError: (error) => {
      setError(error.message || 'Failed to assign work order');
      setIsLoading(false);
    },
    update: (cache, { data }) => {
      logger.debug('Assignment cache update triggered', { workOrderId: data?.assignWorkOrder?.id });

      if (data?.assignWorkOrder) {
        logger.debug('Updating cache for assigned work order', { workOrderId: workOrder.id });

        // Simple and reliable cache update: write the new data directly
        try {
          cache.writeQuery({
            query: GET_WORK_ORDER,
            variables: { id: workOrder.id },
            data: {
              workOrder: data.assignWorkOrder
            }
          });
          logger.debug('Direct work-order cache write completed', { workOrderId: workOrder.id });
        } catch (error) {
          logger.debug('Direct work-order cache write failed', error);

          // Fallback: evict and refetch
          try {
            cache.evict({
              fieldName: 'workOrder',
              args: { id: workOrder.id }
            });
            cache.gc();
            logger.debug('Work-order cache eviction completed as fallback', { workOrderId: workOrder.id });
          } catch (evictError) {
            logger.warn('Work-order cache eviction fallback failed', evictError);
          }
        }
      }
    }
  });

  // Initialize assignedToId when workOrder changes
  useEffect(() => {
    if (workOrder?.assignedTeamId) {
      setAssignedTeamId(String(workOrder.assignedTeamId));
      setAssignmentType('team');
    } else if (workOrder?.assignedToId) {
      setAssignedToId(workOrder.assignedToId);
      setAssignmentType('person');
    }
  }, [workOrder]);

  // Filter users to only show those with appropriate roles and matching areas, then sort alphabetically by first name
  const eligibleUsers = usersData?.users?.filter(user => {
    // Must have appropriate role (now includes ADMIN with area assignments)
    if (!canBeAssignedWorkOrders(user.role, user.areas)) {
      return false;
    }

    // Must be assigned to the same area as the property
    const propertyArea = workOrder?.property?.area;
    if (propertyArea && user.areas && Array.isArray(user.areas)) {
      return user.areas.includes(propertyArea);
    }

    // If property has no area or user has no areas, include them for backward compatibility
    return true;
  }).sort((a, b) => {
    // Extract first name (assuming name format is "First Last" or just "First")
    const firstNameA = a.name?.split(' ')[0]?.toLowerCase() || '';
    const firstNameB = b.name?.split(' ')[0]?.toLowerCase() || '';
    return firstNameA.localeCompare(firstNameB);
  }) || [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    logger.debug('Submitting work-order assignment', {
      workOrderId: workOrder.id,
      assignedToId: assignmentType === 'person' && assignedToId ? parseInt(assignedToId) : null,
      assignedTeamId: assignmentType === 'team' && assignedTeamId ? parseInt(assignedTeamId, 10) : null,
    });

    try {
      const result = await assignWorkOrder({
        variables: {
          input: {
            id: workOrder.id,
            assignedToId: assignmentType === 'person' && assignedToId ? parseInt(assignedToId) : null,
            assignedTeamId: assignmentType === 'team' && assignedTeamId ? parseInt(assignedTeamId, 10) : null,
          }
        }
      });

      logger.debug('Assignment mutation returned', { workOrderId: result.data?.assignWorkOrder?.id });
    } catch (err) {
      logger.error('Error assigning work order', err);
      setError(err.message || 'Failed to assign work order');
      setIsLoading(false);
    }
  };

  // Find the currently assigned user (if any)
  const assignedUser = assignedToId ?
    eligibleUsers.find(user => user.id.toString() === assignedToId.toString()) : null;

  const emptyMessage = workOrder?.property?.area ?
    `No users available for ${workOrder.property.area.replace(/_/g, ' ').toLowerCase()} area` :
    'No users available for this property';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">Assign Work Order</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-md">
              {error}
            </div>
          )}

          {workOrder?.guestAlertsEnabled && !workOrder?.guestAssignmentAlertProcessed && (
            <div className="mb-4 rounded-md border border-teal-300 bg-teal-50 p-3 text-sm text-teal-900">
              <strong>Guest Alerts are on.</strong> Assigning the first team member will send the guest one assignment update. Later reassignments will not notify the guest.
            </div>
          )}

          <div className="mb-4 flex gap-4 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="assignment-type"
                checked={assignmentType === 'person'}
                onChange={() => setAssignmentType('person')}
              />
              Individual
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="radio"
                name="assignment-type"
                checked={assignmentType === 'team'}
                onChange={() => setAssignmentType('team')}
              />
              Team
            </label>
          </div>

          {assignmentType === 'team' ? (
            <div className="mb-4">
              <label htmlFor="assignedTeamId" className="block text-sm font-medium text-gray-700 mb-1">
                {workOrderType === WorkOrderType.HOUSEKEEPING
                  ? 'Assign to Housekeeping Team'
                  : 'Assign to Property Management Team'}
              </label>
              <select
                id="assignedTeamId"
                value={assignedTeamId}
                onChange={(event) => setAssignedTeamId(event.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Select a team</option>
                {(teamsData?.activeTeams || [])
                  .filter((team) => team.department)
                  .map((team) => (
                    <option key={team.id} value={String(team.id)}>{team.name}</option>
                  ))}
              </select>
            </div>
          ) : (
          <div className="mb-4">
            <label htmlFor="assignedToId" className="block text-sm font-medium text-gray-700 mb-1">
              Assign to User
              {workOrder?.property?.area && (
                <span className="text-xs font-normal text-gray-500 ml-2">
                  (showing {workOrder.property.area.replace(/_/g, ' ').toLowerCase()} area staff only)
                </span>
              )}
            </label>

            <SearchableUserSelect
              users={eligibleUsers}
              selectedUserId={assignedToId}
              onSelect={setAssignedToId}
              isLoading={usersLoading}
              placeholder="Search or select a user..."
              emptyMessage={emptyMessage}
            />

            {/* Show the assigned user's role if selected */}
            {assignedUser && (
              <div className="mt-3 p-3 bg-blue-50 rounded-md">
                <div className="text-sm">
                  <span className="font-medium text-blue-700">Selected User Role: </span>
                  <span className="text-blue-800">{UserRoleLabels[assignedUser.role] || assignedUser.role}</span>
                </div>
                <p className="mt-1 text-xs text-blue-600">
                  The work order will automatically be assigned to this role
                </p>
              </div>
            )}
          </div>
          )}

          <div className="flex justify-end space-x-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Assigning...' : (assignmentType === 'team' ? assignedTeamId : assignedToId) ? 'Assign Work Order' : 'Unassign Work Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignWorkOrderModal;
